import { useState } from 'react';
import {
  SearchableConversationFilterInput,
  SearchableConversationSortableFields
} from 'generated-graphql/types';
import {
  useGetApplicationStageUpdatesByConversationLazyQuery,
  useSearchConversationsQuery
} from 'generated-graphql/hooks/apollo-hooks';
import { SortInput } from 'common/hooks';
import { useCampaignNeedsReviewNotification } from './useCampaignNeedsReviewNotification';
import { useCampaignConversationsSubscriptions } from './useCampaignConversationsSubscriptions';
import { SearchConversationsItem } from 'modules/conversations/types';
import { sanitizeObject } from 'utils/fomatting';
import { extractQueryListItems } from 'core/utils/apollo/extractListItems';
import { useAmplifyEventSubscribe } from 'core/services/amplify/events/useAmplifyEventSubscribe';

type Props = {
  campaignId: string;
  filter: SearchableConversationFilterInput;
  limit: number;
  sort: SortInput<SearchableConversationSortableFields>;
  from: number;
};

export const useCampaignConversationsWithSubscriptions = ({
  campaignId,
  filter,
  limit,
  from,
  sort
}: Props) => {
  const [conversations, setConversations] = useState<SearchConversationsItem[]>(
    []
  );

  const { data, loading, refetch } = useSearchConversationsQuery({
    variables: {
      from,
      sort,
      limit,
      filter
    },
    onCompleted: (data) => {
      const items = extractQueryListItems(data);
      setConversations(items);
    }
  });
  const { data: viewEvents } = useAmplifyEventSubscribe({
    channelType: 'CampaignViewConversation',
    channelParams: {
      campaignId
    }
  });

  const [getApplicationStageUpdates] =
    useGetApplicationStageUpdatesByConversationLazyQuery();

  useCampaignNeedsReviewNotification({ conversations });

  const createConversation = (newConversation: SearchConversationsItem) => {
    setConversations((prevConversations) => [
      ...prevConversations,
      sanitizeObject(newConversation)
    ]);
  };

  const updateConversation = async (
    updatedConversation: SearchConversationsItem
  ) => {
    const result = await getApplicationStageUpdates({
      variables: { conversationId: updatedConversation.id }
    });

    const applicationStageUpdatesByConversation =
      result.data?.getApplicationStageUpdatesByConversation;

    let applicationStageUpdates = updatedConversation.applicationStageUpdates;

    if (
      (applicationStageUpdatesByConversation?.items.length || 0) >
      (updatedConversation.applicationStageUpdates?.items.length || 0)
    ) {
      applicationStageUpdates = applicationStageUpdatesByConversation;
    }

    setConversations((prevConversations) => {
      const index = prevConversations.findIndex(
        (conversation) => conversation.id === updatedConversation.id
      );

      const conversation = prevConversations[index];
      if (!conversation) {
        return [...prevConversations, sanitizeObject(updatedConversation)];
      }

      const newConversations = [...prevConversations];
      newConversations[index] = {
        ...conversation,
        ...sanitizeObject(updatedConversation),
        applicationStageUpdates
      };

      if (
        conversation.applicationStage !==
          updatedConversation.applicationStage &&
        !updatedConversation.applicationStage
      ) {
        newConversations[index].applicationStage = null;
      }

      return newConversations;
    });
  };

  useCampaignConversationsSubscriptions({
    campaignId,
    createConversation,
    updateConversation
  });

  // Merge conversations with view information
  const conversationsWithViewInfo = conversations.map((conversation) => {
    const viewInfo = viewEvents.find(
      (event) => event.payload.conversationId === conversation.id
    );
    return {
      ...conversation,
      viewInfo
    };
  });

  return {
    conversations: conversationsWithViewInfo,
    total: data?.searchConversations?.total ?? 0,
    loading,
    refetch
  };
};
